import { Controller, type Control, type UseFormSetValue } from "react-hook-form"

import { FilterBadgeList } from "@/components/search/FindSimilarButton"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { FilterFormData } from "@/lib/search-filter-model"

interface SkillsFiltersSectionProps {
  canFindSimilarKeywords: boolean
  control: Control<FilterFormData>
  formValues: FilterFormData
  isFindingSimilarKeywords: boolean
  isFindingSimilarSkills: boolean
  onAddKeyword: () => void
  onFindSimilarKeywords: () => Promise<void>
  onAddSkill: () => void
  onFindSimilarSkills: () => Promise<void>
  onRemoveKeyword: (keyword: string) => void
  onRemoveSkill: (skill: string) => void
  setValue: UseFormSetValue<FilterFormData>
}

export function SkillsFiltersSection({
  canFindSimilarKeywords,
  control,
  formValues,
  isFindingSimilarKeywords,
  isFindingSimilarSkills,
  onAddKeyword,
  onFindSimilarKeywords,
  onAddSkill,
  onFindSimilarSkills,
  onRemoveKeyword,
  onRemoveSkill,
  setValue,
}: SkillsFiltersSectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-teal-600">Skills</Label>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="p-0 text-teal-600"
            onClick={() => setValue("skills", [], { shouldDirty: true })}
          >
            Clear all
          </Button>
        </div>
        <Controller
          name="skillInput"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Start typing — select from the list, or just hit enter..."
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  onAddSkill()
                }
              }}
            />
          )}
        />
        <FilterBadgeList
          items={formValues.skills}
          onFindSimilar={onFindSimilarSkills}
          onRemove={onRemoveSkill}
          isPending={isFindingSimilarSkills}
          disabled={formValues.skills.length === 0}
        />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-teal-600">Keywords the ideal profile should have</Label>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="p-0 text-teal-600"
            onClick={() => setValue("keywords", [], { shouldDirty: true })}
          >
            Clear all
          </Button>
        </div>
        <Controller
          name="keywordsInput"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Start typing — select from the list, or just hit enter..."
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  onAddKeyword()
                }
              }}
            />
          )}
        />
        <FilterBadgeList
          items={formValues.keywords}
          onFindSimilar={onFindSimilarKeywords}
          onRemove={onRemoveKeyword}
          isPending={isFindingSimilarKeywords}
          disabled={formValues.keywords.length === 0 || !canFindSimilarKeywords}
        />
      </div>
    </div>
  )
}
